import { useState, useRef, useCallback, RefObject } from 'react';
import type { LayoutChangeEvent } from 'react-native';

/**
 * Defines the shape of the dimensions returned by the hook.
 */
export interface Dimensions {
  width: number;
  height: number;
}

/**
 * The unified property object to be spread onto the component (e.g., View or div).
 * - Web (React): will include 'ref'
 * - Native (React Native): will include 'onLayout'
 *
 * It uses a generic type T for the element/component type (e.g., HTMLDivElement or View).
 */
export interface DimensionProps<T = Element> {
  ref?: RefObject<T>;
  onLayout?: (event: LayoutChangeEvent) => void;
  // You might add an 'id' or 'key' here if needed for cross-platform debugging
}

/**
 * The core logic hook structure.
 *
 * @returns [Dimensions, (width: number, height: number) => void] The dimensions and the updater function.
 */
export function useResizeObserverCore(): [
  Dimensions, 
  (width: number, height: number) => void
] {
  const [dimensions, setDimensions] = useState<Dimensions>({
    width: 0,
    height: 0,
  });

  const updateDimensions = useCallback(
    (newWidth: number, newHeight: number) => {
      // Only update state if the dimensions have actually changed
      if (newWidth !== dimensions.width || newHeight !== dimensions.height) {
        setDimensions({ width: newWidth, height: newHeight });
      }
    },
    [dimensions.width, dimensions.height]
  );

  return [dimensions, updateDimensions];
}